// src/pages/ingresos/IntakesHistoryPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { supabase } from "../../utils/supabase";
import IntakeInstrumentModal from "./components/IntakeInstrumentModal";

export default function IntakesHistoryPage() {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [clientFilter, setClientFilter] = useState("");

  const [clients, setClients] = useState([]);
  const [editingIntake, setEditingIntake] = useState(null);

  // =============================
  // Cargar clientes (filtro)
  // =============================
  useEffect(() => {
    async function loadClients() {
      const { data, error } = await supabase
        .from("clients")
        .select("id, name")
        .order("name");

      if (!error && data) setClients(data);
    }
    loadClients();
  }, []);

  // =============================
  // Cargar historial de Intakes
  // =============================
  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setErr("");

        // 🧹 Primero: limpiar intakes vacíos de más de 5 minutos
        try {
          await supabase.rpc("cleanup_empty_intakes", { p_minutes: 5 });
        } catch (e) {
          console.error("[IntakesHistoryPage] cleanup_empty_intakes error", e);
        }

        // Luego: cargar la vista de historial
        const { data, error } = await supabase
          .from("v_intakes_history")
          .select("*")
          .order("created_at", { ascending: false });

        if (!active) return;
        if (error) throw error;

        setRows(data || []);
      } catch (e) {
        console.error("[IntakesHistoryPage] load error", e);
        if (active) setErr(e.message || "Error al cargar historial.");
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, []);

  // =============================
  // FILTROS
  // =============================
  const filtered = useMemo(() => {
    let list = [...rows];

    // Excluir intakes vacíos (0 instrumentos)
    list = list.filter((r) => r.instrument_count > 0);

    if (clientFilter) {
      list = list.filter((r) => r.client_id === clientFilter);
    }

    if (search.trim()) {
      const t = search.trim().toLowerCase();
      list = list.filter((r) => {
        const mix = [
          r.intake_code,
          r.client_name,
          r.tag_list,
          r.serial_list,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return mix.includes(t);
      });
    }

    if (dateFrom) {
      const from = new Date(dateFrom);
      list = list.filter((r) => new Date(r.created_at) >= from);
    }

    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59);
      list = list.filter((r) => new Date(r.created_at) <= to);
    }

    return list;
  }, [rows, search, dateFrom, dateTo, clientFilter]);

  function openPrint(id) {
    window.open(`/ingresos/comprobante?id=${encodeURIComponent(id)}`, "_blank");
  }

  function openEdit(row) {
    setEditingIntake({
      intakeId: row.intake_id,
      clientId: row.client_id,
      clientName: row.client_name,
    });
  }

  return (
    <>
      {/* HEADER */}
      <div className="p-6">
        <h1 className="text-2xl font-semibold text-white">Historial de Intakes</h1>
        <p className="text-slate-400 mt-1">
          Consultá todos los ingresos por cliente, fecha o código. También podés reimprimir comprobantes.
        </p>
        {err && (
          <p className="mt-2 text-sm text-red-400">
            {err}
          </p>
        )}
      </div>

      {/* FILTROS */}
      <div className="px-6 grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <input
          className="px-3 py-2 rounded bg-black/40 border border-white/10 text-white"
          placeholder="Buscar por código, cliente, tag, serie..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="px-3 py-2 rounded bg-black/40 border border-white/10 text-white"
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value)}
        >
          <option value="">Todos los clientes</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="px-3 py-2 rounded bg-black/40 border border-white/10 text-white"
        />

        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="px-3 py-2 rounded bg-black/40 border border-white/10 text-white"
        />
      </div>

      {/* TABLA */}
      <div className="px-6 pb-6">
        <div className="rounded-xl overflow-hidden border border-white/10 bg-black/40">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm text-white">
            <thead className="bg-black/60">
              <tr>
                <th className="px-2 py-2 text-left">Intake</th>
                <th className="px-2 py-2 text-left">Cliente</th>
                <th className="px-2 py-2 text-left">Instrumentos</th>
                <th className="px-2 py-2 text-left">Fecha</th>
                <th className="px-2 py-2 text-left">Estado</th>
                <th className="px-2 py-2 text-left"></th>
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-3 py-4 text-slate-400">
                    Cargando historial…
                  </td>
                </tr>
              )}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-4 text-slate-400">
                    No se encontraron resultados.
                  </td>
                </tr>
              )}

              {!loading &&
                filtered.map((row) => (
                  <tr key={row.intake_id} className="border-t border-white/5">
                    <td className="px-2 py-2">{row.intake_code}</td>
                    <td className="px-2 py-2">{row.client_name}</td>
                    <td className="px-2 py-2">
                      {row.instrument_count} instrumento
                      {row.instrument_count === 1 ? "" : "s"}
                    </td>
                    <td className="px-2 py-2">
                      {row.created_at
                        ? new Date(row.created_at).toLocaleDateString("es-AR")
                        : "-"}
                    </td>

                    <td className="px-2 py-2">
                      {row.has_os ? (
                        <span className="text-emerald-400">Asignado a OS</span>
                      ) : (
                        <span className="text-yellow-400">Pendiente</span>
                      )}
                    </td>

                    <td className="px-2 py-2 flex gap-2">
                      <button
                        className="px-2 py-1 bg-sky-600 hover:bg-sky-500 rounded text-xs"
                        onClick={() => openPrint(row.intake_id)}
                      >
                        Imprimir
                      </button>

                      {!row.has_os && (
                        <button
                          className="px-2 py-1 bg-emerald-600 hover:bg-emerald-500 rounded text-xs"
                          onClick={() => openEdit(row)}
                        >
                          Editar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      {/* MODAL PARA EDITAR */}
      {editingIntake && (
        <IntakeInstrumentModal
          open={true}
          intakeId={editingIntake.intakeId}
          client={{
            id: editingIntake.clientId,
            name: editingIntake.clientName,
          }}
          onClose={() => setEditingIntake(null)}
          mode="intake"
        />
      )}
    </>
  );
}
